import React, { useEffect, useState, useRef } from 'react';
import { useGameContext } from '../../contexts/GameContext';
import { ANIMATION_INTERVAL_MS } from '../../constants';
import { useSettings } from '../../contexts/SettingsContext';

const ChordDisplay: React.FC<{
  task: any;
  taskIndex: number;
  prevTaskIndex: number;
  position: string;
  getColor: string;
}> = ({ task, taskIndex, prevTaskIndex, position, getColor }) => {
  const elementRef = useRef<HTMLDivElement>(null);
  const shouldAnimate = prevTaskIndex !== null && taskIndex !== prevTaskIndex;
  const { chordNotationType } = useSettings();

  const getTransformClass = () => {
    if (shouldAnimate) {
      if (position === "next") return "opacity-0";
      if (position === "prev2") return "opacity-100";
      return "translate-x-full";
    }
    else {
      if (position === "next") return "opacity-100";
      if (position === "prev2") return "opacity-0";
      return "translate-x-0";
    }
  };

  const getEasingClass = () => {
    if (position === "next") {
      return "ease-in";
    }
    return "ease-out";
  };

  useEffect(() => {
    const el = elementRef.current;
    if (!el || !shouldAnimate) {
      return;
    } else {
      setTimeout(() => {
        if (position === "next") {
          el.style.opacity = "1";
        } else if (position === "prev2") {
          el.style.opacity = "0";
        } else if (position !== "prev2") {
          el.style.transform = 'translateX(0)';
        }
      }, ANIMATION_INTERVAL_MS);
    }
  }, [taskIndex, shouldAnimate, position]);

  return (
    <div
      key={`${position}-${taskIndex}`}
      className={`w-32 text-center transition-all duration-200 ${getEasingClass()} transform
        ${true ? getTransformClass() : ''}`}
      ref={elementRef}
    >
      <div
        className={`text-4xl font-medium ${getColor}`}
        dangerouslySetInnerHTML={task ? { __html: task.chord.toString(chordNotationType) } :
          { __html: position === "current" ? 'Loading...' : "-" }}
      >
      </div>
    </div>
  );
};

const ChordQueue: React.FC = () => {
  const { tasks, taskIndex } = useGameContext();
  const [prevTaskIndex, setPrevTaskIndex] = useState(taskIndex);

  // Track when taskIndex changes to trigger animation
  useEffect(() => {
    if (taskIndex !== prevTaskIndex) {
      const timer = setTimeout(() => {
        setPrevTaskIndex(taskIndex);
      }, 500);

      return () => clearTimeout(timer);
    }
  }, [taskIndex, prevTaskIndex]);

  // Get current task and next task
  const previous2Task = tasks.get(taskIndex - 2);
  const previousTask = tasks.get(taskIndex - 1);
  const currentTask = tasks.get(taskIndex);
  const nextTask = tasks.get(taskIndex + 1);

  // Helper functions to determine states
  const isClosed = currentTask?.isClosed || false;
  const isFailed = currentTask?.isFailed || false;

  // UI helper functions
  const getMessage = () => {
    if (!currentTask) return "Loading...";

    if (isClosed) {
      if (isFailed) return "Good!";
      return "Great!";
    }

    if (isFailed) return "Play this chord!";
    return "Play this chord!";
  };

  const getMessageColor = () =>
    isClosed ? 'text-green-500' : '';

  const getCurrentChordColor = () =>
    isClosed ? 'text-green-500' :
    isFailed ? 'text-red-500' : '';

  return (
    <div className="flex flex-col items-center justify-center p-4 w-full">
      {/* Status message */}
      <div className={`mb-6 text-2xl font-medium ${getMessageColor()}`}>
        {getMessage()}
      </div>

      {/* Chord display container */}
      <div className="w-full max-w-2xl">
        {/* Fixed position labels */}
        <div className="flex justify-center items-center"> {/* Changed mb-0 to -mb-3 for negative margin */}
          <div className="w-32 text-center text-2xl uppercase tracking-wider text-gray-500 dark:text-gray-400">
          </div>
          <div className="w-32 text-center text-2xl uppercase tracking-wider text-gray-500 dark:text-gray-400">
            ▼
          </div>
          <div className="w-32 text-center text-2xl uppercase tracking-wider text-gray-500 dark:text-gray-400">
          </div>
        </div>

        {/* Chord values with animation */}
        <div className="relative h-24 flex justify-center items-center">
          {/* Chord container with fixed width to maintain spacing */}
          <div className="flex justify-center items-center space-x-8 relative">

            {/* Previous chord position - contains both previous2 and previous */}
            <div className="w-32 relative">
              {/* Previous2 chord (fades out) */}
              {previous2Task && (
                <div className="absolute inset-0">
                  <ChordDisplay
                    task={previous2Task}
                    taskIndex={taskIndex}
                    prevTaskIndex={prevTaskIndex}
                    position="prev2"
                    getColor="text-gray-400 dark:text-gray-500"
                  />
                </div>
              )}

              {/* Previous chord */}
              <ChordDisplay
                task={previousTask}
                taskIndex={taskIndex}
                prevTaskIndex={prevTaskIndex}
                position="prev"
                getColor="text-gray-400 dark:text-gray-500"
              />
            </div>


            {/* Current chord */}
            <ChordDisplay
              task={currentTask}
              taskIndex={taskIndex}
              prevTaskIndex={prevTaskIndex}
              position="current"
              getColor={getCurrentChordColor()}
            />

            {/* Next chord */}
            <ChordDisplay
              task={nextTask}
              taskIndex={taskIndex}
              prevTaskIndex={prevTaskIndex}
              position="next"
              getColor="text-gray-400 dark:text-gray-500"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChordQueue;
